import React, { createContext, useContext, useState, useEffect } from 'react';
import { translations, languages, getLanguageByCode } from '../locales';

const LanguageContext = createContext(null);

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const [currentLanguage, setCurrentLanguage] = useState(() => {
    const saved = localStorage.getItem('alphabit_language');
    return saved || 'it';
  });

  const [dir, setDir] = useState('ltr');

  // Listen for language sync events from AuthContext
  useEffect(() => {
    const handleLanguageSync = (event) => {
      const newLang = event.detail;
      if (newLang && translations[newLang] && newLang !== currentLanguage) {
        setCurrentLanguage(newLang);
      }
    };

    window.addEventListener('languageSync', handleLanguageSync);
    return () => window.removeEventListener('languageSync', handleLanguageSync);
  }, [currentLanguage]);

  // Also check localStorage on mount in case it was updated
  useEffect(() => {
    const checkStoredLanguage = () => {
      const stored = localStorage.getItem('alphabit_language');
      if (stored && translations[stored] && stored !== currentLanguage) {
        setCurrentLanguage(stored);
      }
    };
    
    // Check immediately and also on storage events
    checkStoredLanguage();
    window.addEventListener('storage', checkStoredLanguage);
    return () => window.removeEventListener('storage', checkStoredLanguage);
  }, []);

  useEffect(() => {
    localStorage.setItem('alphabit_language', currentLanguage);
    const lang = getLanguageByCode(currentLanguage);
    setDir(lang.dir);
    
    // Set document direction for RTL support
    document.documentElement.dir = lang.dir;
    document.documentElement.lang = currentLanguage;
  }, [currentLanguage]);

  const t = (key) => {
    const keys = key.split('.');
    let value = translations[currentLanguage];
    
    for (const k of keys) {
      if (value && typeof value === 'object' && k in value) {
        value = value[k];
      } else {
        // Fallback to Italian if key not found
        value = translations['it'];
        for (const fallbackKey of keys) {
          if (value && typeof value === 'object' && fallbackKey in value) {
            value = value[fallbackKey];
          } else {
            return key; // Return key if not found
          }
        }
        break;
      }
    }
    
    return typeof value === 'string' ? value : key;
  };

  const changeLanguage = (langCode) => {
    if (translations[langCode]) {
      setCurrentLanguage(langCode);
      // Mark that user manually changed language during this session
      sessionStorage.setItem('alphabit_language_manual', 'true');
    }
  };

  return (
    <LanguageContext.Provider value={{
      currentLanguage,
      changeLanguage,
      t,
      dir,
      languages,
      isRTL: dir === 'rtl'
    }}>
      {children}
    </LanguageContext.Provider>
  );
};
